import { DialogDOMUtils } from './dom_utils'
import { createLogger } from '~/utils/logger'

const log = createLogger('DialogCleanup')

// Handles cleanup operations for dialog elements
export class DialogCleanupService {
  markClosed(dialogEl) {
    try {
      if (!dialogEl) return
      dialogEl.setAttribute('data-state', 'closed')

      const { backdrop, panel } = DialogDOMUtils.getDialogStructure(dialogEl)
      
      if (backdrop) {
        backdrop.setAttribute('data-state', 'closed')
        backdrop.style.pointerEvents = 'none'
      }

      if (panel) {
        panel.setAttribute('data-state', 'closed')
        panel.style.pointerEvents = 'none'
      }

      dialogEl.style.pointerEvents = 'none'

      const elementsWithState = DialogDOMUtils.getElementsWithState(dialogEl)
      elementsWithState.forEach(el => el.setAttribute('data-state', 'closed'))
    } catch (error) {
      log.warn('markClosed error:', error)
    }
  }

  disablePointerEvents(el) {
    try { if (el) el.style.pointerEvents = 'none' } catch (e) {
      log.warn('disablePointerEvents error:', e)
    }
  }

  scheduleCleanup(dialogEl, { animationDuration = 200, onCleanupComplete } = {}) {
    setTimeout(() => {
      try {
        onCleanupComplete && onCleanupComplete(dialogEl)
      } catch (error) {
        log.warn('scheduleCleanup error:', error)
      }
    }, animationDuration)
  }

  purgeOrphanOverlays({ isActiveRootFn, animationDuration = 200, onDisablePointerEvents, onRefreshLayers } = {}) {
    try {
      const stack = (window.__dialogStack || [])
      const inStack = new Set(stack)
      const roots = DialogDOMUtils.getAllDialogRoots()
      
      roots.forEach(root => {
        try {
          if (isActiveRootFn && isActiveRootFn(root, inStack)) return

          const { fixedNodes, hasBackdrop } = DialogDOMUtils.getDialogStructure(root)
          const allClosedOrGone = fixedNodes.length === 0 || fixedNodes.every(n => {
            const state = n.getAttribute('data-state')
            const display = (n.style && n.style.display) || ''
            return state === 'closed' || display === 'none'
          })
          const rootClosed = root.getAttribute('data-state') === 'closed'

          if (!hasBackdrop && fixedNodes.length === 0) return
          if (inStack.has(root) && !(rootClosed || allClosedOrGone)) return

          try { onDisablePointerEvents && onDisablePointerEvents(root) } catch (_) {}
          setTimeout(() => {
            try {
              fixedNodes.forEach(n => { try { n.remove() } catch (_) {} })
              const hFulls = DialogDOMUtils.getHFullElements(root)
              hFulls.forEach(n => { try { n.remove() } catch (_) {} })
            } catch (_) {}
            try { onRefreshLayers && onRefreshLayers() } catch (_) {}
          }, animationDuration)
        } catch (_) {}
      })
    } catch (e) {
      log.warn('purgeOrphanOverlays failed:', e)
    }
  }

  cleanupAllDialogsForElement(el, { animationDuration = 200, onRefreshLayers } = {}) {
    if (!el) return
    try {
      const roots = DialogDOMUtils.getAllDialogRoots()
      roots.forEach(root => {
        if (!root.contains(el)) return

        const { fixedNodes, hasBackdrop, hasOverlay } = DialogDOMUtils.getDialogStructure(root)
        if (!hasOverlay) return

        try { root.style.pointerEvents = 'none' } catch (_) {}
        fixedNodes.forEach(node => {
          try {
            node.style.pointerEvents = 'none'
            node.setAttribute('data-state', 'closed')
          } catch (_) {}
        })

        setTimeout(() => {
          try {
            // Remove backdrops specifically
            const backdrops = root.querySelectorAll(DialogDOMUtils.SELECTORS.BACKDROP)
            backdrops.forEach(b => b.remove())
            Array.from(root.children).forEach(child => {
              const cn = (child.className || '')
              if (cn.includes('fixed') || cn.includes('inset-0')) {
                try { child.remove() } catch (_) {}
              }
            })
          } catch (_) {}
          try { root.style.pointerEvents = '' } catch (_) {}
          try { onRefreshLayers && onRefreshLayers() } catch (_) {}
        }, animationDuration)
      })
    } catch (e) {
      log.warn('cleanupAllDialogsForElement failed:', e)
    }
  }
}

